import { useT } from '../context/I18nContext';
import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api';
import type {
  CampusView, InstitutionView, DailyRosterView, RosterStudent, ShortageReport,
} from '../lib/types';
import { ModuleStrip } from '../components/ModuleStrip';

type Tab = 'register' | 'shortage';
const STATUSES = ['Present', 'Absent', 'Late', 'Excused'] as const;
const todayIso = () => new Date().toISOString().slice(0, 10);

export function AttendancePage() {
  const t = useT();
  const [tab, setTab] = useState<Tab>('register');
  const [institutions, setInstitutions] = useState<InstitutionView[]>([]);
  const [campuses, setCampuses] = useState<CampusView[]>([]);
  const [institutionId, setInstitutionId] = useState('');

  useEffect(() => {
    Promise.all([
      api.get<InstitutionView[]>('/api/institutions'),
      api.get<CampusView[]>('/api/campuses'),
    ]).then(([i, c]) => {
      setInstitutions(i); setCampuses(c);
      if (i.length === 1) setInstitutionId(i[0].id);
    }).catch(() => {});
  }, []);

  const campusName = (id: string) => campuses.find((c) => c.id === id)?.name ?? '';

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Daily</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>{t('Attendance')}</h1>
      </div>

      <ModuleStrip module="attendance" />

      {/* institution picker + tabs */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        <select className="input" value={institutionId} onChange={(e) => setInstitutionId(e.target.value)} style={{ maxWidth: 320 }}>
          <option value="">Select an institution</option>
          {institutions.map((i) => (
            <option key={i.id} value={i.id}>{i.name}{campusName(i.campusId) ? ` · ${campusName(i.campusId)}` : ''}</option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          <TabBtn active={tab === 'register'} onClick={() => setTab('register')}>Register</TabBtn>
          <TabBtn active={tab === 'shortage'} onClick={() => setTab('shortage')}>Shortage report</TabBtn>
        </div>
      </div>

      {!institutionId ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-2)' }}>
          Select an institution to begin.
        </div>
      ) : tab === 'register' ? (
        <Register institutionId={institutionId} />
      ) : (
        <Shortage institutionId={institutionId} />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button className="btn" onClick={onClick} style={{
      padding: '7px 14px',
      background: active ? 'var(--brand)' : 'var(--surface)',
      color: active ? '#fff' : 'var(--ink-2)',
      borderColor: active ? 'var(--brand)' : 'var(--line)',
    }}>{children}</button>
  );
}

// ---------------- Register ----------------
function Register({ institutionId }: { institutionId: string }) {
  const [date, setDate] = useState(todayIso());
  const [classLabel, setClassLabel] = useState('');
  const [roster, setRoster] = useState<DailyRosterView | null>(null);
  const [marks, setMarks] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setMsg(null);
    try {
      const params = new URLSearchParams({ institutionId, date });
      if (classLabel.trim()) params.set('classLabel', classLabel.trim());
      const r = await api.get<DailyRosterView>(`/api/attendance/roster?${params}`);
      setRoster(r);
      // seed marks from roster (defaults to Present, or existing marks)
      setMarks(Object.fromEntries(r.students.map((s) => [s.studentId, s.status])));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the roster.');
    } finally { setLoading(false); }
  }, [institutionId, date, classLabel]);

  useEffect(() => { load(); }, [load]);

  const setStatus = (studentId: string, status: string) =>
    setMarks((m) => ({ ...m, [studentId]: status }));

  const markAll = (status: string) =>
    setMarks(Object.fromEntries((roster?.students ?? []).map((s) => [s.studentId, status])));

  const save = async () => {
    if (!roster) return;
    setSaving(true); setError(null); setMsg(null);
    try {
      const entries = roster.students.map((s) => ({ studentId: s.studentId, status: marks[s.studentId] ?? 'Present', remark: null }));
      const res = await api.post<{ marked: number }>('/api/attendance/mark', {
        institutionId, date, classLabel: classLabel.trim() || null, entries,
      });
      setMsg(`Saved ${res.marked} student${res.marked === 1 ? '' : 's'}. Guardians of absentees will be notified.`);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save attendance.');
    } finally { setSaving(false); }
  };

  const counts = (roster?.students ?? []).reduce((acc, s) => {
    const st = marks[s.studentId] ?? 'Present';
    acc[st] = (acc[st] ?? 0) + 1; return acc;
  }, {} as Record<string, number>);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label className="label">Date</label>
          <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 170 }} />
        </div>
        <div>
          <label className="label">Class / section (optional)</label>
          <input className="input" value={classLabel} onChange={(e) => setClassLabel(e.target.value)}
            placeholder="e.g. Class 9-A" style={{ width: 200 }} />
        </div>
        <button className="btn" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Load'}</button>
      </div>

      {roster?.alreadyMarked && (
        <div className="pill pill-muted" style={{ padding: '6px 12px', borderRadius: 'var(--r-md)', marginBottom: 12 }}>
          Attendance already recorded for this day — editing will update it.
        </div>
      )}
      {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}
      {msg && <div className="pill pill-ok" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 12, justifyContent: 'flex-start' }}>{msg}</div>}

      {roster && roster.students.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>Quick:</span>
            <button className="btn" style={{ padding: '5px 10px', fontSize: 13 }} onClick={() => markAll('Present')}>All present</button>
            <button className="btn" style={{ padding: '5px 10px', fontSize: 13 }} onClick={() => markAll('Absent')}>All absent</button>
            <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--ink-2)' }}>
              <span style={{ color: 'var(--ok)' }}>{counts.Present ?? 0} present</span>
              {' · '}<span style={{ color: 'var(--danger)' }}>{counts.Absent ?? 0} absent</span>
              {counts.Late ? ` · ${counts.Late} late` : ''}{counts.Excused ? ` · ${counts.Excused} excused` : ''}
            </span>
          </div>

          <div className="card" style={{ overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--line)', textAlign: 'left' }}>
                  <Th>Admission #</Th><Th>Name</Th><Th>Class</Th><Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {roster.students.map((s: RosterStudent) => (
                  <tr key={s.studentId} style={{ borderBottom: '1px solid var(--line-2)' }}>
                    <Td><span className="mono">{s.admissionNumber}</span></Td>
                    <Td><strong>{s.fullName}</strong></Td>
                    <Td>{s.classLabel ?? '—'}</Td>
                    <Td>
                      <div style={{ display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
                        {STATUSES.map((st) => {
                          const active = (marks[s.studentId] ?? 'Present') === st;
                          const color = st === 'Present' ? 'var(--ok)' : st === 'Absent' ? 'var(--danger)' : st === 'Late' ? 'var(--warn)' : 'var(--ink-3)';
                          return (
                            <button key={st} onClick={() => setStatus(s.studentId, st)}
                              style={{
                                border: 'none', padding: '6px 12px', fontSize: 13, fontWeight: active ? 700 : 500,
                                cursor: 'pointer', background: active ? color : 'transparent',
                                color: active ? '#fff' : 'var(--ink-3)',
                              }}>
                              {st[0]}
                            </button>
                          );
                        })}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-accent" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save attendance'}
            </button>
          </div>
        </>
      )}

      {roster && roster.students.length === 0 && !loading && (
        <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--ink-2)' }}>
          No students {classLabel ? `in "${classLabel}"` : 'in this institution'} yet.
        </div>
      )}
    </div>
  );
}

// ---------------- Shortage ----------------
function Shortage({ institutionId }: { institutionId: string }) {
  const monthAgo = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 10); };
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(todayIso());
  const [report, setReport] = useState<ShortageReport | null>(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ institutionId, from, to });
      setReport(await api.get<ShortageReport>(`/api/attendance/shortage?${params}`));
    } finally { setLoading(false); }
  }, [institutionId, from, to]);

  useEffect(() => { run(); }, [run]);

  const shortList = report?.rows.filter((r) => r.short) ?? [];

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div><label className="label">From</label><input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ width: 170 }} /></div>
        <div><label className="label">To</label><input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ width: 170 }} /></div>
        <button className="btn" onClick={run} disabled={loading}>{loading ? 'Running…' : 'Run report'}</button>
        {report && <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--ink-2)' }}>Threshold: below {report.shortagePercent}%</span>}
      </div>

      {report && (
        <>
          {shortList.length > 0 && (
            <div className="pill pill-warn" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', marginBottom: 12 }}>
              {shortList.length} student{shortList.length === 1 ? '' : 's'} below {report.shortagePercent}%
            </div>
          )}
          <div className="card" style={{ overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--line)', textAlign: 'left' }}>
                  <Th>Admission #</Th><Th>Name</Th><Th>Class</Th><Th align="right">Held</Th><Th align="right">Present</Th><Th align="right">%</Th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.studentId} style={{ borderBottom: '1px solid var(--line-2)', background: r.short ? 'color-mix(in srgb, var(--warn) 8%, transparent)' : 'transparent' }}>
                    <Td><span className="mono">{r.admissionNumber}</span></Td>
                    <Td><strong>{r.fullName}</strong></Td>
                    <Td>{r.classLabel ?? '—'}</Td>
                    <Td align="right"><span className="mono">{r.held}</span></Td>
                    <Td align="right"><span className="mono">{r.present}</span></Td>
                    <Td align="right"><span className="mono" style={{ fontWeight: 700, color: r.short ? 'var(--warn)' : 'var(--ok)' }}>{r.percent}%</span></Td>
                  </tr>
                ))}
                {report.rows.length === 0 && <tr><Td colSpan={6} muted>No attendance data in this range.</Td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Th({ children, align }: { children: ReactNode; align?: 'right' }) {
  return <th style={{ padding: '12px 16px', fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '.02em', textAlign: align ?? 'left' }}>{children}</th>;
}
function Td({ children, colSpan, muted, align }: { children: ReactNode; colSpan?: number; muted?: boolean; align?: 'right' }) {
  return <td colSpan={colSpan} style={{ padding: '12px 16px', color: muted ? 'var(--ink-3)' : 'var(--ink)', textAlign: muted ? 'center' : (align ?? 'left') }}>{children}</td>;
}
